/**
 * シート・ダイアログ（ネイティブの <dialog> + showModal）。
 * フォーカスの閉じ込めと Esc で閉じるのはブラウザに任せる。閉じたら開く前の要素へフォーカスを戻す。
 * 背景（::backdrop）を押しても閉じる。
 */
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useI18n } from "../useI18n.js";
import { Icon } from "./Icon.js";

export function Sheet({ open, onClose, title, children, variant = "sheet", className, initialFocus }: {
  open: boolean; onClose: () => void; title: ReactNode; children: ReactNode;
  variant?: "sheet" | "dialog"; className?: string;
  /** 開いたときに最初にフォーカスする要素のセレクタ（無ければ閉じるボタン）。 */
  initialFocus?: string;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const downOnBackdrop = useRef(false);
  const titleId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      d.showModal();
      const target = initialFocus ? d.querySelector<HTMLElement>(initialFocus) : null;
      target?.focus();
    } else if (!open && d.open) {
      d.close();
    }
  }, [open, initialFocus]);

  // 閉じたら開いた要素へフォーカスを戻す
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onClosed = () => {
      const el = opener.current as HTMLElement | null;
      if (el && document.contains(el)) el.focus({ preventScroll: true });
    };
    d.addEventListener("close", onClosed);
    return () => d.removeEventListener("close", onClosed);
  }, []);

  return (
    <dialog ref={ref} className={`${variant}${className ? ` ${className}` : ""}`} aria-labelledby={titleId}
            onCancel={(e) => { e.preventDefault(); onClose(); }}
            // 背景を押したときだけ閉じる。中で押し始めて背景で離した（文字を選んだ等）ときは閉じない
            onPointerDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
            onClick={(e) => { if (e.target === e.currentTarget && downOnBackdrop.current) onClose(); downOnBackdrop.current = false; }}>
      {open && (
        <div className="sheet-inner">
          <div className="sheet-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t("common.close")}>
              <Icon name="close" />
            </button>
          </div>
          <div className="sheet-body">{children}</div>
        </div>
      )}
    </dialog>
  );
}
